import type { ExtremaTarget } from '@type/extrema';
import type { BarPoint, MaidrLayer } from '@type/grammar';
import type { Movable } from '@type/movable';
import type { AudioState, BrailleState, DescriptionState, TextState } from '@type/state';
import type { Dimension } from './abstract';
import { Orientation } from '@type/grammar';
import { MathUtil } from '@util/math';
import { Svg } from '@util/svg';
import { AbstractTrace } from './abstract';
import { MovableGrid } from './movable';

export abstract class AbstractBarPlot<T extends BarPoint> extends AbstractTrace {
  protected readonly movable: Movable;

  protected readonly points: T[][];
  protected readonly barValues: number[][];
  protected readonly highlightValues: SVGElement[][] | null;
  protected highlightCenters:
    | { x: number; y: number; row: number; col: number; element: SVGElement }[]
    | null;

  protected readonly orientation: Orientation;
  protected readonly min: number[];
  protected readonly max: number[];
  protected readonly supportsExtrema = true;

  protected constructor(layer: MaidrLayer, points: T[][]) {
    super(layer);

    this.points = points;
    this.orientation = layer.orientation ?? Orientation.VERTICAL;

    this.barValues = points.map(row =>
      row.map(point =>
        this.orientation === Orientation.VERTICAL
          ? Number(point.y)
          : Number(point.x),
      ),
    );
    this.min = this.barValues.map(row => MathUtil.safeMin(row));
    this.max = this.barValues.map(row => MathUtil.safeMax(row));
    this.highlightValues = this.mapToSvgElements(layer.selectors as string);
    this.highlightCenters = this.mapSvgElementsToCenters();
    this.movable = new MovableGrid<T>(this.points);
  }

  /**
   * Cleans up bar plot resources including points and min/max arrays.
   */
  public dispose(): void {
    this.points.length = 0;

    this.min.length = 0;
    this.max.length = 0;

    super.dispose();
  }

  protected get audio(): AudioState {
    const isVertical = this.orientation === Orientation.VERTICAL;

    const value = isVertical
      ? this.barValues[this.row][this.col]
      : this.barValues[this.col][this.row];

    return {
      freq: {
        min: MathUtil.safeMin(this.min),
        max: MathUtil.safeMax(this.max),
        raw: value,
      },
      panning: {
        x: isVertical ? this.col : this.row,
        y: isVertical ? this.row : this.col,
        rows: isVertical ? this.barValues.length : this.barValues[this.col].length,
        cols: isVertical ? this.barValues[this.row].length : this.barValues.length,
      },
      group: isVertical ? this.row : this.col,
    };
  }

  protected get braille(): BrailleState {
    return {
      empty: false,
      id: this.id,
      values: this.barValues,
      min: this.min,
      max: this.max,
      row: this.row,
      col: this.col,
    };
  }

  protected get text(): TextState {
    const isVertical = this.orientation === Orientation.VERTICAL;
    const point = this.points[this.row][this.col];

    const mainLabel = isVertical ? this.xAxis : this.yAxis;
    const mainValue = isVertical ? point.x : point.y;

    const crossLabel = isVertical ? this.yAxis : this.xAxis;
    const crossValue = isVertical ? point.y : point.x;

    return {
      main: { label: mainLabel, value: mainValue },
      cross: { label: crossLabel, value: crossValue },
      mainAxis: isVertical ? 'x' : 'y',
      crossAxis: isVertical ? 'y' : 'x',
    };
  }

  /**
   * Gets the description state for the bar plot trace.
   * @returns The description state containing chart metadata and data table
   */
  public get description(): DescriptionState {
    const isVertical = this.orientation === Orientation.VERTICAL;
    const stats: DescriptionState['stats'] = [
      { label: 'Number of bars', value: this.points[0].length },
      { label: 'Min value', value: MathUtil.safeMin(this.min) },
      { label: 'Max value', value: MathUtil.safeMax(this.max) },
    ];

    if (this.points.length > 1) {
      stats.push({ label: 'Number of groups', value: this.points.length });
    }

    const headers = isVertical
      ? [this.xAxis, this.yAxis]
      : [this.yAxis, this.xAxis];

    const rows: (string | number)[][] = this.points[0].map((p) => {
      const main = isVertical ? p.x : p.y;
      const cross = isVertical ? p.y : p.x;
      return [main, cross];
    });

    return {
      chartType: this.getChartTypeLabel(),
      title: this.title,
      axes: this.getDescriptionAxes(),
      stats,
      dataTable: { headers, rows },
    };
  }

  protected get dimension(): Dimension {
    return {
      rows: this.barValues.length,
      cols: this.barValues[this.row].length,
    };
  }

  protected get values(): number[][] {
    return this.barValues;
  }

  protected mapToSvgElements(selector?: string): SVGElement[][] | null {
    if (!selector) {
      return null;
    }

    const queried = Svg.selectAllElements(selector);
    const svgElements = [queried];

    if (svgElements.length !== this.points.length) {
      return null;
    }

    for (let row = 0; row < this.points.length; row++) {
      if (svgElements[row].length === this.points[row].length) {
        continue; // exact match — nothing to do
      }

      // SVG renderers (e.g. Plotly) may omit zero-height bars from the DOM.
      // When fewer SVG elements than data points exist, align them by
      // assigning each non-zero data point the next queried element and
      // inserting an invisible placeholder for zero-value points.
      if (svgElements[row].length < this.points[row].length) {
        const aligned: SVGElement[] = [];
        let svgIdx = 0;
        for (let col = 0; col < this.points[row].length; col++) {
          const value = this.orientation === Orientation.VERTICAL
            ? Number(this.points[row][col].y)
            : Number(this.points[row][col].x);
          if (value !== 0 && svgIdx < svgElements[row].length) {
            aligned.push(svgElements[row][svgIdx++]);
          } else {
            aligned.push(Svg.createEmptyElement());
          }
        }
        svgElements[row] = aligned;
      } else {
        return null; // more SVG elements than data points — unexpected
      }
    }

    return svgElements;
  }

  /**
   * Maps SVG elements to their center coordinates for proximity detection.
   * @returns Array of center points with element references or null if no elements exist
   */
  protected mapSvgElementsToCenters():
    | { x: number; y: number; row: number; col: number; element: SVGElement }[]
    | null {
    const svgElements: (SVGElement | SVGElement[])[][] | null = this.highlightValues;

    if (!svgElements) {
      return null;
    }

    const centers: {
      x: number;
      y: number;
      row: number;
      col: number;
      element: SVGElement;
    }[] = [];
    for (let row = 0; row < svgElements.length; row++) {
      for (let col = 0; col < svgElements[row].length; col++) {
        const element = svgElements[row][col];
        const targetElement = Array.isArray(element) ? element[0] : element;
        const bbox = targetElement.getBoundingClientRect();
        if (targetElement) {
          centers.push({
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
            row,
            col,
            element: targetElement,
          });
        }
      }
    }

    return centers;
  }

  /**
   * Finds the nearest bar element at the specified coordinates.
   * @param x - The x-coordinate
   * @param y - The y-coordinate
   * @returns Object containing the element and its position, or null if not found
   */
  public findNearestPoint(
    x: number,
    y: number,
  ): { element: SVGElement; row: number; col: number } | null {
    // we differ from the base implementation (which is to loop through centers and return one),
    // as sometimes the closest center is not the bar we clicked on
    // so instead, we just do the hard thing and loop through all highlightValues
    if (!this.highlightValues || this.highlightValues.length === 0) {
      return null;
    }

    // loop through all highlightValues, and check bounding boxes against x, y
    for (let row = 0; row < this.highlightValues.length; row++) {
      const rowElements = this.highlightValues[row];
      // Skip undefined or empty rows
      if (!rowElements || rowElements.length === 0) {
        continue;
      }
      for (let col = 0; col < rowElements.length; col++) {
        const element = rowElements[col];
        // Skip undefined elements
        if (!element) {
          continue;
        }
        const targetElement = Array.isArray(element) ? element[0] : element;
        // Skip if targetElement is invalid or an empty placeholder
        if (!targetElement || !targetElement.getBoundingClientRect) {
          continue;
        }
        const bbox = targetElement.getBoundingClientRect();
        // Skip elements with no size (empty placeholders)
        if (bbox.width === 0 && bbox.height === 0) {
          continue;
        }
        if (
          x >= bbox.x
          && x <= bbox.x + bbox.width
          && y >= bbox.y
          && y <= bbox.y + bbox.height
        ) {
          return { element: targetElement, row, col };
        }
      }
    }

    return null;
  }
}

/**
 * Concrete implementation of a bar trace for standard bar charts.
 * Supports extrema navigation and hover interactions.
 */
export class BarTrace extends AbstractBarPlot<BarPoint> {
  /**
   * Checks if coordinates are within the bounding box of the bar element.
   * @param x - The x-coordinate
   * @param y - The y-coordinate
   * @param element - Object containing the SVG element and its position
   * @param element.element - The SVG element to check bounds against
   * @param element.row - The row position of the element
   * @param element.col - The column position of the element
   * @returns True if coordinates are within bounds, false otherwise
   */
  public isPointInBounds(
    x: number,
    y: number,
    { element, row: _row, col: _col }: { element: SVGElement; row: number; col: number },
  ): boolean {
    // check if x y is within the bounding box of the element
    const bbox = element.getBoundingClientRect();
    return (
      x >= bbox.x
      && x <= bbox.x + bbox.width
      && y >= bbox.y
      && y <= bbox.y + bbox.height
    );
  }

  /**
   * Constructs a new BarTrace instance.
   * @param layer - The MAIDR layer configuration
   */
  public constructor(layer: MaidrLayer) {
    super(layer, [layer.data as BarPoint[]]);
  }

  /**
   * Get extrema targets for the current bar plot trace
   * Returns min and max values within the current group
   * @returns Array of extrema targets for navigation
   */
  public override getExtremaTargets(): ExtremaTarget[] {
    const targets: ExtremaTarget[] = [];
    const currentGroup = this.row;

    if (currentGroup < 0 || currentGroup >= this.barValues.length) {
      return targets;
    }

    const groupValues = this.barValues[currentGroup];
    if (!groupValues || groupValues.length === 0) {
      return targets;
    }

    // Use pre-computed min/max values instead of recalculating
    const groupMin = this.min[currentGroup];
    const groupMax = this.max[currentGroup];

    // Find indices of min/max values
    const maxIndex = groupValues.indexOf(groupMax);
    const minIndex = groupValues.indexOf(groupMin);

    // Inline raw x-value lookup using currentGroup (avoids hidden this.row dependency)
    const maxPoint = this.points[currentGroup]?.[maxIndex];
    const minPoint = this.points[currentGroup]?.[minIndex];
    const maxXValue = maxPoint
      ? (this.orientation === Orientation.VERTICAL ? maxPoint.x : maxPoint.y)
      : undefined;
    const minXValue = minPoint
      ? (this.orientation === Orientation.VERTICAL ? minPoint.x : minPoint.y)
      : undefined;

    // Add max target
    targets.push({
      label: `Max Bar at ${this.getPointLabel(maxIndex)}`,
      value: groupMax,
      pointIndex: maxIndex,
      segment: 'bar',
      type: 'max',
      navigationType: 'point',
      xValue: maxXValue,
    });

    // Add min target
    targets.push({
      label: `Min Bar at ${this.getPointLabel(minIndex)}`,
      value: groupMin,
      pointIndex: minIndex,
      segment: 'bar',
      type: 'min',
      navigationType: 'point',
      xValue: minXValue,
    });

    return targets;
  }

  /**
   * Navigate to a specific extrema target
   * @param target The extrema target to navigate to
   */
  public override navigateToExtrema(target: ExtremaTarget): void {
    // Update the current point index (column)
    this.col = target.pointIndex;

    // Use common finalization method
    this.finalizeNavigation();
  }

  /**
   * Gets a clean label for a specific bar point.
   * @param pointIndex - The index of the point
   * @returns A clean label for the point
   */
  private getPointLabel(pointIndex: number): string {
    if (this.points[this.row] && this.points[this.row][pointIndex]) {
      const point = this.points[this.row][pointIndex];

      if (this.orientation === Orientation.VERTICAL) {
        return `${point.x}`;
      } else {
        return `${point.y}`;
      }
    }

    return `Point ${pointIndex}`;
  }

  /**
   * Updates the visual position of the current point ensuring it's within bounds.
   */
  protected updateVisualPointPosition(): void {
    // Ensure we're within bounds
    const { row: safeRow, col: safeCol } = this.getSafeIndices();
    this.row = safeRow;
    this.col = safeCol;
  }

  /**
   * Moves to the next bar that matches the comparison criteria in rotor mode.
   * @param direction - The direction to move (left or right)
   * @param type - The comparison type (lower or higher)
   * @returns True if a target was found, false otherwise
   */
  public override moveToNextCompareValue(direction: 'left' | 'right', type: 'lower' | 'higher'): boolean {
    const currentGroup = this.row;
    if (currentGroup < 0 || currentGroup >= this.barValues.length) {
      return false;
    }

    const groupValues = this.barValues[currentGroup];
    if (!groupValues || groupValues.length === 0) {
      return false;
    }

    const currentIndex = this.col;
    const step = direction === 'right' ? 1 : -1;
    let i = currentIndex + step;

    while (i >= 0 && i < groupValues.length) {
      if (this.compare(groupValues[i], groupValues[currentIndex], type)) {
        this.col = i;
        this.updateVisualPointPosition();
        this.notifyStateUpdate();
        return true;
      }
      i += step;
    }
    this.notifyRotorBounds();
    return false;
  }
}
